// Presentation helpers shared by the Seire overview (/seire) and the platform detail (/seire/:platformId).
// The status itself still comes from the existing pipes (PlatformLevelStatusPipe, CheckpointLevelStatusPipe,
// SslValidUntilPipe); this file only decides how those results look.
import {CheckpointStatusOrbsEnum} from '../classes/checkpoint-status-orbs.enum';
import {Platform} from '../classes/platform';
import {Server} from '../classes/server';
import {UrlPrefixStrippingPipe} from '../pipes/url-prefix-stripping.pipe';
import {SslValidUntilPipe} from '../pipes/ssl-valid-until.pipe';
import {CheckStripTone, Env, Tone} from '../shared/ui';

// Thresholds from the design (README open point 4: chosen, not yet confirmed).
/** SSL certificates expiring within this many days are orange. */
export const SSL_WARN_DAYS = 30;
/** Servers with at least this many pending updates get an orange pill. */
export const SERVER_UPDATES_WARN = 150;
/** OS releases below this major version (e.g. 22.04) are orange. */
export const SERVER_OS_MIN_MAJOR = 24;

export interface StatusDisplay {
    /** Sort order, worst first. */
    rank: number;
    tone: Tone;
    strip: CheckStripTone;
    label: string;
}

/** How the status orbs of the existing pipes are shown: dot + label, and the bar color in the check strip. */
export const ORB_DISPLAY: Record<string, StatusDisplay> = {
    [CheckpointStatusOrbsEnum.error]: {rank: 0, tone: 'error', strip: 'error', label: 'Storing'},
    [CheckpointStatusOrbsEnum.warning]: {rank: 1, tone: 'warn', strip: 'warn', label: 'Waarschuwing'},
    [CheckpointStatusOrbsEnum.alert]: {rank: 2, tone: 'warn', strip: 'alert', label: 'Opgeschort'},
    [CheckpointStatusOrbsEnum.success]: {rank: 3, tone: 'ok', strip: 'ok', label: 'Goed'},
    [CheckpointStatusOrbsEnum.disabled]: {rank: 4, tone: 'neutral', strip: 'none', label: 'Uitgeschakeld'},
    [CheckpointStatusOrbsEnum.inactive]: {rank: 5, tone: 'neutral', strip: 'none', label: 'Geen checks'},
};

export function orbDisplay(orb: string): StatusDisplay {
    return ORB_DISPLAY[orb] ?? ORB_DISPLAY[CheckpointStatusOrbsEnum.inactive];
}

/** Whole calendar days from today to the date: 0 today, -1 yesterday, 30 in a month (same as DaysFromNowPipe). */
export function calendarDaysFromToday(value: Date | string | null | undefined): number | null {
    if (!value) {
        return null;
    }
    const date = new Date(value);
    if (isNaN(date.getTime())) {
        return null;
    }
    const now = new Date();
    const day = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
    const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
    return Math.round((day - today) / 86_400_000);
}

const sslValidUntil = new SslValidUntilPipe();

/** Text tone of an SSL expiry date: red from the existing rule (SslValidUntilPipe), orange below SSL_WARN_DAYS. */
export function sslTone(value: Date | string | null | undefined): 'error' | 'warn' | null {
    const days = calendarDaysFromToday(value);
    if (days === null) {
        return null;
    }
    if (sslValidUntil.transform(value) === 'text-danger') {
        return 'error';
    }
    return days < SSL_WARN_DAYS ? 'warn' : null;
}

export interface EnvUrl {
    env: Env;
    url: string;
    /** URL without protocol and trailing slash. */
    label: string;
}

const ENV_NAMES: Record<Env, string> = {P: 'Productie', A: 'Acceptatie', T: 'Test', L: 'Local'};
const urlPrefixStripping = new UrlPrefixStrippingPipe();

export function envName(env: Env): string {
    return ENV_NAMES[env];
}

/** The URLs that are set on a platform, in P/A/T/L order. */
export function platformEnvUrls(platform: Platform): EnvUrl[] {
    const urls: [Env, string][] = [
        ['P', platform?.prod_url],
        ['A', platform?.acc_url],
        ['T', platform?.test_url],
        ['L', platform?.local_url],
    ];
    return urls
        .filter(([, url]) => !!url?.trim())
        .map(([env, url]) => ({env, url, label: urlPrefixStripping.transform(url)}));
}

export interface ServerDisplay {
    server: Server;
    moduleKnown: boolean;
    /** Apache runs another PHP version than the CLI. */
    phpDiffers: boolean;
    osOutdated: boolean;
    manyUpdates: boolean;
    /** Last report is older than yesterday. */
    stale: boolean;
}

export function serverDisplay(server: Server): ServerDisplay {
    const os = parseFloat(server.os);
    const updates = parseInt(server.updates, 10);
    const days = calendarDaysFromToday(server.updated_at);
    return {
        server,
        moduleKnown: !!server.apache_module && server.apache_module !== 'unknown',
        phpDiffers: (server.apache_php ?? '') !== (server.php ?? ''),
        osOutdated: !isNaN(os) && os < SERVER_OS_MIN_MAJOR,
        manyUpdates: !isNaN(updates) && updates >= SERVER_UPDATES_WARN,
        stale: days !== null && days < -1,
    };
}
